/**
 * DV-PROJ-001: Create Project Modal Component
 *
 * Modal for creating new projects with form validation
 */

import React, { useState } from 'react';
import { X, FolderPlus, AlertCircle } from 'lucide-react';
import { useCreateProjectMutation } from '@/hooks/useProjectsQuery';
import type { CreateProjectRequest } from '@/types/projects';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

interface FormErrors {
  name?: string;
  description?: string;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateProjectRequest>({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const createProjectMutation = useCreateProjectMutation();

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '' });
      setErrors({});
    }
  }, [isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name is required and must be reasonable length
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Project name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Project name must be less than 100 characters';
    }

    // Description is optional but has length limit
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await createProjectMutation.mutateAsync({
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined
      });

      onSuccess(result.data.project.id);
    } catch (error) {
      console.error('Failed to create project:', error);
      // Error is handled by React Query and can be shown in UI
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-black/90 backdrop-blur-xl border border-cyan-500/20 rounded-lg shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
              <FolderPlus className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-light text-white">New Project</h2>
              <p className="text-xs text-white/50 font-mono">Create a project to manage assets</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter project name"
                className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                           focus:outline-none transition-colors font-mono text-sm ${
                  errors.name
                    ? 'border-red-500/50 focus:border-red-500/70'
                    : 'border-white/20 focus:border-cyan-500/50'
                }`}
                maxLength={100}
              />
              {errors.name && (
                <div className="flex items-center gap-2 mt-1 text-red-400 text-xs">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Project Description */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional project description"
                rows={3}
                className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                           focus:outline-none transition-colors font-mono text-sm resize-none ${
                  errors.description
                    ? 'border-red-500/50 focus:border-red-500/70'
                    : 'border-white/20 focus:border-cyan-500/50'
                }`}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.description ? (
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.description}
                  </div>
                ) : (
                  <div />
                )}
                <div className="text-xs text-white/40 font-mono">
                  {formData.description?.length || 0}/500
                </div>
              </div>
            </div>

            {/* Error Message */}
            {createProjectMutation.isError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to create project. Please try again.
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={createProjectMutation.isPending}
              className="px-4 py-2 text-sm border border-white/20 text-white/70 rounded
                         hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createProjectMutation.isPending || !formData.name.trim()}
              className="px-4 py-2 text-sm bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 rounded
                         hover:bg-cyan-500/30 transition-colors disabled:opacity-50
                         flex items-center gap-2"
            >
              {createProjectMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FolderPlus className="w-4 h-4" />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectModal;